<script lang="ts">
  import { format } from "date-fns";
  import { slide } from "svelte/transition";
  import { cubicInOut } from "svelte/easing";
  import type { InvoiceRequestDetails } from "../../../../api/transactions";
  import { t } from "../../../../helpers/translations";
  import Stack from "../../../Stack.svelte";
  import TextNew from "../../../TextNew.svelte";
  import IconButton from "../../../buttons/IconButton.svelte";
  import Icon from "../../../icons/Icon.svelte";
  import AvatarIcon from "../../../icons/AvatarIcon.svelte";
  import MissingInvoiceIcon from "../../../icons/MissingInvoiceIcon.svelte";
  import { isValidEmail } from "../../../../helpers/utils";

  export let emails: string[] = [];
  export let orderNumber: string;
  export let requestDetails: InvoiceRequestDetails = null;
  export let expanded = false;
  export let emailTarget: string;
  export let primaryOption = false;

  let validEmail = true;
  let inputActive = false;
  let inputElement: HTMLInputElement;

  $: fullHeight = primaryOption && !requestDetails;

  function toggleExpand(event: MouseEvent) {
    event.stopPropagation();
    expanded = !expanded;
  }

  function focusInput() {
    inputActive = true;
    setTimeout(() => {
      inputElement.focus();
    }, 0);
  }

  function updateEmail() {
    emailTarget = inputElement.value;
    if (isValidEmail(inputElement.value)) {
      validEmail = true;
      inputActive = false;
    } else {
      validEmail = false;
    }
  }

  function handleInput(event: KeyboardEvent) {
    if (event.key === "Enter") {
      updateEmail();
    }
  }

  function handleBlur() {
    updateEmail();
  }
</script>

<div class="container" class:full-height={fullHeight}>
  <button class="header" on:click={requestDetails ? undefined : toggleExpand}>
    <MissingInvoiceIcon />
    <Stack vertical padding="0 32px 0 16px">
      {#if requestDetails}
        <TextNew size="m">invoice requested</TextNew>
        <TextNew size="s" --rk-text-color="var(--rk-text-low)"
          >{`by ${requestDetails.request_user} on ${format(
            new Date(requestDetails.request_date),
            "dd/MM/yy"
          )}, please call your supplier if you have not received this`}</TextNew
        >
      {:else}
        <TextNew size="m">{t("requestInvoice.row.title")}</TextNew>
        <TextNew size="s" --rk-text-color="var(--rk-text-low)"
          >{t("requestInvoice.row.subtitle")}</TextNew
        >
      {/if}
    </Stack>
    {#if !requestDetails && !fullHeight}
      <div class="right-action">
        <IconButton name={expanded ? "close" : "arrowRight"} on:click={toggleExpand} />
      </div>
    {/if}
  </button>
  {#if (expanded || fullHeight) && !requestDetails}
    <div
      class="email-container"
      class:full-height={fullHeight}
      transition:slide={{ duration: 300, easing: cubicInOut }}
    >
      <div class="email">
        <div class="email-header">
          <AvatarIcon />
          <Stack vertical padding="0 10px" overflow="hidden">
            <p class="email-text">new email</p>
            <p class="email-text text-low">
              {`to ${emails.join(",")}`}
            </p>
          </Stack>
        </div>
        <div class="email-content">
          <p class="email-text">
            {t("requestInvoice.email.header", 1, { orderNumber: orderNumber })}
            {#if inputActive}
              <input
                id="textInput"
                type="text"
                bind:this={inputElement}
                on:keyup={handleInput}
                on:blur={handleBlur}
                placeholder={t("requestInvoice.email.placeholder")}
                class="editable"
                class:invalid={!validEmail}
              />
            {:else}
              <button on:click={focusInput} class="edit-button"
                >{emailTarget}
                <div class="edit-icon">
                  <Icon name="edit" />
                </div>
              </button>
            {/if}
          </p>
          <br />
          <p class="email-text">
            {t("requestInvoice.email.footer")}
          </p>
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  .container {
    display: flex;
    flex-direction: column;
    background-color: var(--rekki-light);
    width: 100%;
    padding: 16px;
    transition: all 0.3s ease-in-out;
  }

  .right-action {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 56px;
    height: 56px;
  }

  .full-height {
    height: 100%;
  }

  .header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    text-align: left;
  }

  .email-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border: solid 1px var(--rk-divider-0);
    border-radius: 8px;
    margin-top: 16px;
    padding: 32px 64px;
    background-color: var(--rk-neutral-0);
    transition: height 0.3s ease-in-out;
  }

  .email {
    display: flex;
    flex-direction: column;
    background-color: var(--rekki-light);
    border-radius: 8px;
    width: 100%;
    padding: 0 16px;
    box-shadow: 0px 4.51px 80.89px 0px rgba(0, 0, 0, 0.045);
    box-shadow: 0px 2px 40px 0px rgba(0, 0, 0, 0.09);
  }

  .email-text {
    font-family: iA Writer Mono S;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0em;
    text-align: left;
  }

  .text-low {
    color: var(--rk-text-low);
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    width: 100%;
  }

  .email-header {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    padding: 16px 0;
    border-bottom: solid 1px var(--rk-divider-0);
  }

  .email-content {
    margin-top: 16px;
    padding: 0 32px 16px 50px;
  }

  .editable {
    font-family: iA Writer Mono S;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0em;
    width: 100%;
    text-align: left;
    border: none;
    background-color: transparent;
    color: var(--rk-text-dark);
  }

  .invalid {
    color: var(--rk-text-error);
  }

  .edit-button {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    color: var(--rk-primary-accent);
  }

  .edit-icon {
    margin-left: 3px;
  }
</style>
